import React, { useState } from "react";
import { Sparkles, X, Database } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import pt from "@/lib/i18n";

const DISMISS_KEY = "nx_demo_banner_dismissed";

const isDismissed = () => {
    try {
        return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
        return false;
    }
};

/**
 * Demo Mode banner — slim copper-tinted strip shown at the top of the dashboard.
 * Communicates that this is a curated demo snapshot, not live production traffic.
 * Dismissible (persisted to localStorage). Super_admin sees the snapshot date and
 * a hint about how to reset the demo data via CLI.
 */
const DemoBanner = ({ user }) => {
    const [dismissed, setDismissed] = useState(isDismissed);

    const handleDismiss = () => {
        try {
            localStorage.setItem(DISMISS_KEY, "1");
        } catch {
            /* ignore */
        }
        setDismissed(true);
    };

    const isSuperAdmin = user?.role === "super_admin";
    const _isEn = (typeof localStorage !== "undefined" && localStorage.getItem("nx_lang") === "en");
    const snapshotDate = new Date().toLocaleDateString(_isEn ? "en-GB" : "pt-PT", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    if (dismissed) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-5 rounded-xl px-4 py-2.5 flex items-center gap-3 flex-wrap"
                style={{
                    background:
                        "linear-gradient(90deg, rgba(200,121,65,0.10) 0%, rgba(232,169,106,0.06) 60%, rgba(200,121,65,0.10) 100%)",
                    border: "1px solid rgba(200,121,65,0.28)",
                }}
                data-testid="demo-banner"
            >
                <div
                    className="rounded-full p-1.5 flex-shrink-0"
                    style={{ background: "rgba(200,121,65,0.18)" }}
                >
                    <Sparkles size={14} className="text-[color:var(--copper-dark)]" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-body text-sm text-[color:var(--slate-deep)] font-medium leading-tight">
                        {pt.demoBanner?.title || "Ambiente de Demonstração · Angola Telecom"}
                    </p>
                    <p className="font-body text-[11px] text-[color:var(--slate-mid)] mt-0.5">
                        {pt.demoBanner?.snapshot || "Snapshot curado"} · {snapshotDate}
                        {isSuperAdmin && (
                            <>
                                {" "}· {pt.demoBanner?.resetHint || "repor via"}{" "}
                                <code className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[color:var(--copper-subtle)] text-[color:var(--copper-dark)]">
                                    python -m scripts.seed_demo --reset
                                </code>
                            </>
                        )}
                    </p>
                </div>
                {isSuperAdmin && (
                    <span
                        className="hidden md:inline-flex items-center gap-1 px-2 py-1 rounded-full font-mono text-[10px] tracking-wider uppercase font-semibold"
                        style={{
                            background: "rgba(46,125,50,0.10)",
                            color: "var(--success)",
                            border: "1px solid rgba(46,125,50,0.20)",
                        }}
                    >
                        <Database size={10} />
                        Seed v1.0
                    </span>
                )}
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="p-1.5 rounded-full hover:bg-[color:var(--copper-subtle)] transition-colors text-[color:var(--slate-mid)]"
                    aria-label="Dismiss demo banner"
                    data-testid="demo-banner-dismiss"
                >
                    <X size={14} />
                </button>
            </motion.div>
        </AnimatePresence>
    );
};

export default DemoBanner;
